import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { fetchRequest } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { postInvalidations } from "@circle-react/lib/queries/posts";
import type { Post } from "@circle-react/types";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { WorkflowsMomentumModal } from "@/react/components/WorkflowsMomentumModal";
import type { ApiError } from "@/react/config/CustomErrors";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";

const REPORT_REASONS = [
  "harassment",
  "spam",
  "incorrect_location",
  "against_guidelines",
  "other",
];

export interface ReportPostModalUIKitComponentProps {
  post: Post;
}

interface FormData {
  flagged_contentable_id: number;
  flagged_contentable_type: string;
  reported_reason_body: string;
  reported_reason_type: string;
}

export const ReportPostModalUIKitComponent = ({
  post,
}: ReportPostModalUIKitComponentProps) => {
  const modal = useModal();
  const toast = useToast();
  const { currentCommunityMember } = usePunditUserContext();

  const handleClose = () => {
    void modal.hide().then(() => modal.remove());
  };

  const handleReport = useMutation<Post, ApiError, FormData>({
    mutationFn: data =>
      fetchRequest<Post>(internalApi.flaggedContent.create(), {
        method: "POST",
        forReactQuery: true,
        body: JSON.stringify({ flagged_content: data }),
      }) as Promise<Post>,
    onSuccess: () => {
      void postInvalidations.allPosts();
      void postInvalidations.postDetails(post.id);

      toast.success(t("report_post_success"));

      if (isCommunityAdmin(currentCommunityMember)) {
        void ModalManager.show(WorkflowsMomentumModal, {
          templateKey: "ai_moderate_new_posts",
        });
      }

      handleClose();
    },
    onError: () => {
      toast.error(t("something_went_wrong"));
    },
  });

  const reportReasonOptions = REPORT_REASONS.map(reason => ({
    value: reason,
    label: t(`flagged_contents.report_reasons.${reason}`),
  }));

  return (
    <Modal
      isOpen={modal.visible}
      onClose={handleClose}
      title={t("flagged_contents.form.report_post")}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Form<FormData>
          defaultValues={{
            flagged_contentable_id: post.id,
            flagged_contentable_type: "Post",
          }}
          onSubmit={data => handleReport.mutate(data)}
        >
          <Modal.Header>
            <Modal.Title size="sm">
              {t("flagged_contents.form.report_post")}
            </Modal.Title>
            <Modal.HeaderActions>
              <Modal.CloseButton onClick={handleClose} />
            </Modal.HeaderActions>
          </Modal.Header>
          <Modal.Body>
            <Form.Item
              name="reported_reason_type"
              label={t("flagged_contents.form.reported_reason_type")}
              description={t(
                "flagged_contents.form.reported_reason_type_description",
              )}
              hideBorder
            >
              <Form.SelectNative
                name="reported_reason_type"
                options={reportReasonOptions}
              />
            </Form.Item>
            <Form.Item
              name="reported_reason_body"
              label={t("flagged_contents.form.reported_reason_body")}
              description={t(
                "flagged_contents.form.reported_reason_body_description",
              )}
              hideBorder
              fullWidth
            >
              <Form.TextArea name="reported_reason_body" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              className="mr-2"
              disabled={handleReport.isPending}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              variant="circle"
              disabled={handleReport.isPending}
              data-testid="report_post_submit"
            >
              {t("report")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Content>
    </Modal>
  );
};
